import React, { useCallback, useRef, useState } from 'react';
import { useInput, useNotify } from 'react-admin';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { DEFAULT_API_ADMIN } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';

/**
 * Upload dedicado, fora do FormData do create — mesmo desenho do
 * `BolaoImageInput`: sobe na hora em `POST .../roletas/upload-image` e grava
 * a URL devolvida no campo do formulário.
 *
 * `kind` diz ao backend como redimensionar: `banner` é a faixa do topo da tela
 * da roleta, `center` é a arte do miolo da roda.
 */
type Kind = 'banner' | 'center';

interface Props {
  source: string;
  label: string;
  kind: Kind;
  aspectRatio?: number;
  helperText?: string;
}

const ASPECT_BY_KIND: Record<Kind, number> = {
  banner: 1080 / 480,
  center: 1,
};

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';

const RoletaImageInput: React.FC<Props> = ({ source, label, kind, aspectRatio, helperText }) => {
  const { field } = useInput({ source });
  const notify = useNotify();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const chainId = getChainId();

  const ratio = aspectRatio ?? ASPECT_BY_KIND[kind];
  const previewUrl = typeof field.value === 'string' ? field.value : '';

  const handleUpload = useCallback(async (file: File) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      notify('Imagem maior que 5MB. Escolha um arquivo menor.', { type: 'warning' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', kind);

      const url = `${DEFAULT_API_ADMIN}/chains/${chainId}/roletas/upload-image`;
      const { json } = await httpClientReactAdmin(
        url,
        { method: 'POST', body: formData },
        true, // isFormData → o httpClient tira o Content-Type e o browser põe o boundary
      );

      const newUrl = json?.data?.url ?? json?.url;
      if (!newUrl) throw new Error('Resposta sem URL');

      field.onChange(newUrl);
      notify('Imagem enviada.', { type: 'success' });
    } catch (err: any) {
      const apiMessage = err?.body?.message || err?.body?.error || err?.message || 'Falha no upload';
      notify(apiMessage, { type: 'error' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [chainId, kind, field, notify]);

  const handleClear = () => field.onChange(null);
  const handlePick = () => inputRef.current?.click();
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <Box sx={{ mb: 2, width: '100%', maxWidth: kind === 'banner' ? 520 : 200 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: String(ratio),
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: previewUrl ? 'transparent' : '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={handlePick}
      >
        {uploading ? (
          <CircularProgress size={28} />
        ) : previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={label}
            sx={{ width: '100%', height: '100%', objectFit: kind === 'banner' ? 'cover' : 'contain' }}
          />
        ) : (
          <Box textAlign="center" sx={{ color: 'text.secondary', px: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 36, opacity: 0.6 }} />
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Clique para escolher uma imagem
            </Typography>
          </Box>
        )}

        {previewUrl && !uploading && (
          <Tooltip title="Remover imagem">
            <IconButton
              size="small"
              onClick={(event) => { event.stopPropagation(); handleClear(); }}
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
        <Button size="small" variant="text" startIcon={<CloudUploadIcon />} onClick={handlePick} disabled={uploading}>
          {previewUrl ? 'Trocar imagem' : 'Enviar imagem'}
        </Button>
        {helperText && (
          <Typography variant="caption" color="text.secondary">{helperText}</Typography>
        )}
      </Box>

      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={handleChange} />
    </Box>
  );
};

export default RoletaImageInput;
